import { z } from "zod";
import { parseTimeOfDayMinutes, resolveDayEndMinutes, TIME_SLOT_DAY_START } from "@carry/shared";
import { PublicIdSchema } from "./common";

/** 便区分境界・配車稼働終端の検証対象 (部分更新のため全て任意)。 */
export type TimeSlotBoundaryTimes = {
  early_morning_until_time?: string | undefined;
  am_until_time?: string | undefined;
  pm_until_time?: string | undefined;
  dispatch_end_time?: string | undefined;
};

export type TimeSlotBoundaryIssue = {
  field: keyof TimeSlotBoundaryTimes;
  message: string;
};

/**
 * 便区分境界と配車の稼働終端の前後関係を検証する (#544 / #640)。
 *
 * 便窓は隣接して連続する (早朝 `00:00〜early` / 午前 `early〜am` / 午後 `am〜pm`) ので、
 * 逆転・同値を許すとその便が消えるか、注文が一点窓に潰れる。
 *
 * #640: **終端の `"00:00"` は当日終端 (24:00)** として解決してから比較する。文字列比較のままだと
 * `"14:00" >= "00:00"` が真になり、午後終端を当日終端にする保存が拒否される。開始時刻としての
 * `"00:00"` (早朝上限) は 0 時のままなので、こちらは従来どおり弾く。
 *
 * 値が渡されていない項目に関わる比較はスキップする (部分更新は呼び出し側で既存値とマージしてから渡す)。
 * 形式不正 (HH:MM でない) は zod のフィールド検証に任せ、ここでは比較しない。
 */
export function validateTimeSlotBoundaryTimes(
  input: TimeSlotBoundaryTimes,
): TimeSlotBoundaryIssue[] {
  const issues: TimeSlotBoundaryIssue[] = [];
  // 早朝上限は「開始 (00:00) より後」でなければ早朝便が消える。終端と違い 00:00 は 0 時のまま。
  const early = parseTimeOfDayMinutes(input.early_morning_until_time);
  const am = parseTimeOfDayMinutes(input.am_until_time);
  const pmEnd = resolveDayEndMinutes(input.pm_until_time);
  const dispatchEnd = resolveDayEndMinutes(input.dispatch_end_time);

  if (early === 0) {
    issues.push({
      field: "early_morning_until_time",
      message: `早朝の上限時刻は ${TIME_SLOT_DAY_START} より後に設定してください`,
    });
  }
  if (early !== null && am !== null && early >= am) {
    issues.push({
      field: "early_morning_until_time",
      message: "早朝の上限時刻は午前の上限時刻より前に設定してください",
    });
  }
  if (am !== null && pmEnd !== null && am >= pmEnd) {
    issues.push({
      field: "am_until_time",
      message: "午前の上限時刻は午後便の終端より前に設定してください",
    });
  }
  // 稼働終端が午前上限以下だと午後便の稼働窓が消え、午後の注文が一切配車できなくなる。
  if (am !== null && dispatchEnd !== null && dispatchEnd <= am) {
    issues.push({
      field: "dispatch_end_time",
      message: "配車の稼働終端は午前の上限時刻より後に設定してください",
    });
  }
  // 稼働終端が便区分の終端を超えると、便区分に属さない時刻へ配達する解を許してしまう。
  if (pmEnd !== null && dispatchEnd !== null && dispatchEnd > pmEnd) {
    issues.push({
      field: "dispatch_end_time",
      message: "配車の稼働終端は午後便の終端以前に設定してください",
    });
  }
  return issues;
}

export const SystemSetting = z.object({
  id: PublicIdSchema,
  office_name: z.string(),
  office_address: z.string(),
  office_latitude: z.string().nullable(),
  office_longitude: z.string().nullable(),
  return_deadline_time: z.string().nullable(),
  default_time_slots: z.array(z.string()).nullable(),
  /** 早朝便の上限時刻 "HH:MM" (この時刻未満を早朝便とする). 便区分判定の境界. */
  early_morning_until_time: z.string(),
  /** 午前便の上限時刻 "HH:MM" (この時刻未満を午前便、以降を午後便とする). */
  am_until_time: z.string(),
  /**
   * 午後便の終端時刻 "HH:MM" (= **請求上の便区分**の終端). #544 で設定値化。
   * #640: `"00:00"` は当日終端 (24:00) を意味し、便区分が 3 枠で 1 日を隙間なく覆う。
   */
  pm_until_time: z.string(),
  /**
   * 配車 (ルート最適化) の稼働終端 "HH:MM" (#640)。デポの稼働を終える時刻。
   * 便区分の終端 (`pm_until_time` = 24:00) とは独立に持ち、最適化の午後窓の右端はこちらで抑える。
   */
  dispatch_end_time: z.string(),
  notes: z.string().nullable(),
  /** pickup → 次 stop の実走時間に掛ける係数 (既定 1.2。OSRM は都市部補正済みのため追加マージン分. #353). */
  pickup_travel_multiplier: z.number(),
  /** pickup → 次 stop の実走時間に加算する余裕分 (分). */
  pickup_travel_buffer_minutes: z.number().int(),
  /** delivery → 次 stop の実走時間に掛ける係数. */
  delivery_travel_multiplier: z.number(),
  /** delivery → 次 stop の実走時間に加算する余裕分 (分). */
  delivery_travel_buffer_minutes: z.number().int(),
  /**
   * 集荷から配達までの上限 (分)。既定 180 (3 時間ルール)。
   * 判定は移動時間補正・現地待機を含む到着予定時刻で行う (#526)。
   */
  max_ride_minutes: z.number().int(),
  /**
   * 延長した注文に使う上限 (分)。既定 300 (5 時間ルール, #767)。
   * 受注入力で「延長」を選んだ注文の `max_ride_minutes` にこの値が入る。
   * 上限と同じく業務上の値のため設定画面からは変更できない (更新入力に載せない)。
   */
  extended_max_ride_minutes: z.number().int(),
  /**
   * 最適化の安全余裕 (分)。solver は「上限 − この値」を実効上限として解く (#526)。
   * 行列計算と実経路計算のずれで、画面の到着予定時刻が上限を超えるのを防ぐ。
   */
  ride_time_safety_margin_minutes: z.number().int(),
  /**
   * 注文書取込で**終了時刻だけ**抽出できたとき、その何分前を開始にするか (分, #657)。
   * 取込のサポート機能であり、人が画面で空にした時刻には適用しない。**0 は補完しない**。
   */
  import_early_arrival_tolerance_minutes: z.number().int(),
  /** 注文書取込で**開始時刻だけ**抽出できたとき、その何分後を終了にするか (分, #657)。 */
  import_late_arrival_tolerance_minutes: z.number().int(),
  /**
   * 配達締切前の到着余裕 (分, #855)。自動配車は配達の到着を「指定終了 − この値」までに収める。
   * 時間幅が余裕以下なら開始も前倒しする。0 で余裕なし。
   */
  delivery_arrival_margin_minutes: z.number().int(),
  /**
   * 午後便の出発前倒し許容幅 (分)。早朝便・午前便に確定ルートを持たないドライバーに限り、
   * 午後便のデポ出発を am_until_time からこの分数だけ前倒しできる (0 = 前倒しなし)。
   */
  /** 請求書発行者の正式名称 (F25)。営業所名 (office_name) とは別に法人名を想定。 */
  invoice_issuer_name: z.string().nullable(),
  invoice_issuer_postal_code: z.string().nullable(),
  invoice_issuer_address: z.string().nullable(),
  invoice_issuer_tel: z.string().nullable(),
  invoice_issuer_fax: z.string().nullable(),
  /** 適格請求書発行事業者の登録番号 (T+13桁)。 */
  invoice_registration_number: z.string().nullable(),
  /** 社印 (捺印) 画像。data URL (data:image/png;base64,...)。請求書 Excel の発行者ブロックに埋め込む */
  invoice_seal_image: z.string().nullable(),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
});

export const UpdateSystemSettingInput = z
  .object({
    office_name: z.string().min(1).optional(),
    office_address: z.string().optional(),
    office_latitude: z.string().nullable().optional(),
    office_longitude: z.string().nullable().optional(),
    return_deadline_time: z.string().nullable().optional(),
    default_time_slots: z.array(z.string()).nullable().optional(),
    early_morning_until_time: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "HH:MM 形式で入力してください")
      .optional(),
    am_until_time: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "HH:MM 形式で入力してください")
      .optional(),
    /** #640: `"00:00"` は当日終端 (24:00) として扱う。正規表現は従来どおり許容済み。 */
    pm_until_time: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "HH:MM 形式で入力してください")
      .optional(),
    /** 配車 (ルート最適化) の稼働終端 (#640)。`"00:00"` は当日終端 (24:00)。 */
    dispatch_end_time: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "HH:MM 形式で入力してください")
      .optional(),
    notes: z.string().nullable().optional(),
    pickup_travel_multiplier: z.number().min(0).optional(),
    pickup_travel_buffer_minutes: z.number().int().min(0).optional(),
    delivery_travel_multiplier: z.number().min(0).optional(),
    delivery_travel_buffer_minutes: z.number().int().min(0).optional(),
    /**
     * 最適化の安全余裕 (分)。既定 0。
     * 上限 (`max_ride_minutes`) は業務上の固定値のため更新入力に載せない (画面から変更不可)。
     */
    ride_time_safety_margin_minutes: z
      .number()
      .int()
      .min(0)
      .max(24 * 60)
      .optional(),
    /**
     * 注文書取込の時刻補完に使う許容幅 (分)。**0 は「補完しない」** (#657)。
     * 開始と終了が同一時刻の枠は optimizer が 1 点の窓として解き、到着がその時刻に一致しない
     * 注文がすべて未割当になるため、幅ゼロの補完は行わず片側指定のまま残す。
     * 同日内に収める前提のため上限は 1 日。
     */
    import_early_arrival_tolerance_minutes: z
      .number()
      .int()
      .min(0)
      .max(24 * 60)
      .optional(),
    import_late_arrival_tolerance_minutes: z
      .number()
      .int()
      .min(0)
      .max(24 * 60)
      .optional(),
    /** 配達締切前の到着余裕 (分, #855)。締切の 2 時間より前を求める運用は想定しない。 */
    delivery_arrival_margin_minutes: z.number().int().min(0).max(120).optional(),
    /** 午後便の出発前倒し許容幅 (分)。0 で前倒しなし (現行挙動)。 */
    invoice_issuer_name: z.string().max(200).nullable().optional(),
    invoice_issuer_postal_code: z.string().max(20).nullable().optional(),
    invoice_issuer_address: z.string().max(500).nullable().optional(),
    invoice_issuer_tel: z.string().max(50).nullable().optional(),
    invoice_issuer_fax: z.string().max(50).nullable().optional(),
    invoice_registration_number: z
      .string()
      .regex(/^T\d{13}$/, "登録番号は T + 13桁の数字で入力してください (例: T1234567890123)")
      .nullable()
      .optional(),
    /** 社印画像 (data URL, PNG/JPEG)。約 500KB (base64 換算) まで */
    invoice_seal_image: z
      .string()
      .regex(/^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/, {
        message: "社印画像は PNG または JPEG の data URL で指定してください",
      })
      .max(700_000, "社印画像は 500KB 以下にしてください")
      .nullable()
      .optional(),
  })
  // 便区分境界・稼働終端の前後関係を保証する。同時更新される場合 (設定画面は常に全項目送る) は
  // ここで弾く。片方のみ更新する API 呼び出しは既存値とのマージ後に handler 側でも検証する
  // (検証規則は validateTimeSlotBoundaryTimes に集約し、両者でズレないようにする)。
  .superRefine((input, ctx) => {
    for (const issue of validateTimeSlotBoundaryTimes(input)) {
      ctx.addIssue({ code: "custom", message: issue.message, path: [issue.field] });
    }
  });

export type SystemSettingDto = z.infer<typeof SystemSetting>;
export type UpdateSystemSettingInputDto = z.infer<typeof UpdateSystemSettingInput>;
